import React, { useState, useEffect } from 'react';
import { Typography, Box, Paper, IconButton, Tooltip } from '@mui/material';
import { useAccount } from '../../contexts/AccountContext';
import { api } from '../../utils/api';
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    TableCell,
} from "../ui/table";
import DeliveryStatsDialog from "./DeliveryStatsDialog";
import './BlastsCSS/SentBlasts.css';
import { Info } from 'lucide-react';
import ScheduledDateDisplay from './ScheduledDateDisplay';

export default function SentBlasts() {
    const [sentBlasts, setSentBlasts] = useState([]);
    const { currentAccountId } = useAccount();
    const [deliveryStatsOpen, setDeliveryStatsOpen] = useState(false);
    const [deliveryStats, setDeliveryStats] = useState({});
    const [error, setError] = useState('');

    useEffect(() => {
        const loadData = async () => {
            const token = localStorage.getItem('authToken');
            if (currentAccountId && token) {
                try {
                    await loadSentBlasts(currentAccountId);
                } catch (error) {
                    console.error('Error loading sent blasts:', error);
                    setError(error.message || 'Failed to load sent blasts');
                }
            }
        };

        loadData();
    }, [currentAccountId]);

    const loadSentBlasts = async (accountId) => {
        try {
            if (!accountId) {
                console.error('No accountId provided to loadSentBlasts');
                setError('No account ID available');
                return;
            }

            const response = await api.get(`/api/blasts/${accountId}`);
            
            if (Array.isArray(response)) {
                // Filter only sent blasts
                const sentBlastsOnly = response.filter(blast => blast.status === "sent");
                setSentBlasts(sentBlastsOnly);
            } else {
                console.error('Invalid response format:', response);
                setError('Invalid data format received from server');
            }
        } catch (error) {
            console.error('Error fetching sent blasts:', error);
            setError(error.message || 'Failed to load sent blasts');
        }
    };


    const handleInfoClick = (blast, event) => {
        if (event) event.stopPropagation();
        
        setDeliveryStats({
            totalAttempted: blast.recipientCount || 0,
            successfulSends: blast.successfulSends || 0,
            failedSends: blast.failedSends || []
        });
        setDeliveryStatsOpen(true);
    };

    const isMobile = window.innerWidth <= 600;

    return (
        <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="bg-gray-100 p-4 rounded-2xl shadow-xl overflow-hidden">
                <Box className="p-4 mb-4 bg-white rounded-lg">
                    <Typography variant="h5" className="font-bold mb-4" style={{ fontWeight: 700 }}>
                        Previously Sent Blasts
                    </Typography>

                    {error && (
                        <Typography color="error" className="mb-4">
                            {error}
                        </Typography>
                    )}

                    {sentBlasts.length === 0 ? (
                        <Typography color="textSecondary" className="mt-4">
                            You haven't sent any blasts yet. Create and send a blast to see the history here!
                        </Typography>
                    ) : (
                        <div className={isMobile ? "blast-table-mobile" : "blast-table-desktop"}>
                            {isMobile ? (
                                sentBlasts.map((blast) => (
                                    <Paper 
                                        key={blast.id} 
                                        className="p-4 mb-4 rounded-lg"
                                    >
                                        <div className="flex flex-col gap-2">
                                            <Typography variant="body1" className="font-medium">
                                                {blast.message}
                                            </Typography>
                                            <div className="flex justify-between text-sm text-gray-600">
                                                <span>
                                                    {blast.scheduledDate ? (
                                                        <>Scheduled: <ScheduledDateDisplay scheduledDate={blast.scheduledDate} /></>
                                                    ) : (
                                                        `Sent: ${new Date(blast.dateSent).toLocaleString()}`
                                                    )}
                                                </span>
                                                <span>Recipients: {blast.recipientCount || 0}</span>
                                            </div>
                                            <div className="flex justify-end">
                                                <IconButton 
                                                    onClick={(e) => handleInfoClick(blast, e)}
                                                    className="text-gray-600 hover:text-gray-900"
                                                >
                                                    <Info className="w-5 h-5" />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </Paper>
                                ))
                            ) : (
                                <Table className="w-full">
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-[35%] font-bold text-lg">Message</TableHead>
                                            <TableHead className="w-[25%] font-bold text-lg">Sent Date</TableHead>
                                            <TableHead className="w-[25%] font-bold text-lg">Recipients</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg text-right">Info</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sentBlasts.map((blast) => (
                                            <TableRow 
                                                key={blast.id}
                                                className="hover:bg-gray-50"
                                            >
                                                <TableCell className="font-medium">
                                                    {blast.message}
                                                </TableCell>
                                                <TableCell>
                                                    {blast.scheduledDate ? (
                                                        <span>
                                                            Scheduled: <ScheduledDateDisplay scheduledDate={blast.scheduledDate} />
                                                        </span>
                                                    ) : (
                                                        new Date(blast.dateSent).toLocaleString()
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {blast.recipientCount || 0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="View Delivery Stats" arrow placement="top">
                                                        <IconButton 
                                                            onClick={(e) => handleInfoClick(blast, e)}
                                                            className="text-gray-600 hover:text-gray-900"
                                                        >
                                                            <Info className="w-5 h-5" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    )}
                </Box>
            </div>

            <DeliveryStatsDialog
                open={deliveryStatsOpen}
                onClose={() => setDeliveryStatsOpen(false)}
                stats={deliveryStats}
            />
        </div>
    );
}